'use client';

import dynamic from 'next/dynamic';

/**
 * Client-side lazy boundaries for the custom-button island views whose deps are
 * genuinely heavy (calendar / standalone-leads — booking + the lead-form's
 * firestore/react-hook-form/recaptcha). The group, music, and video buttons are
 * NOT here — they server-render (group: pure CSS `cqw` widths; music: static
 * card + plain `<a>` CTA; video: static thumbnail facade), keeping only their
 * genuinely-interactive parts as client pieces (the group's children, music's
 * 30s audio preview, video's tap-to-mount iframe — which is browser-loaded, not
 * a JS bundle). The collection button is NOT here either — its visitor surface
 * is server-rendered + streamed (see
 * the registry's `renderCollectionButton` host slot) so its products / clips
 * stay crawlable and ship no generated API client; only its owner-editor variant
 * stays a client component.
 *
 * The server dispatch ({@link ./registry}) imports the island views FROM HERE
 * instead of from each variant directly. Because this module is `'use client'`,
 * `next/dynamic({ ssr: false })` is a REAL lazy boundary (a `dynamic` call in a
 * Server Component is inert) — so each island view, plus its transitive client
 * deps (the lead-form's firestore + react-hook-form + recaptcha, the calendar's
 * booking flow), code-splits into its own chunk that loads ONLY when the
 * dispatcher actually renders that variant. The result is pulled OUT of the
 * eager visitor-chrome first-load: a creator with only link/text buttons ships
 * zero island JS, and firebase loads on the first API request, not at first paint.
 *
 * Trade-off: `ssr: false` means these islands render after hydration (no server
 * HTML for them) — acceptable, since they are the genuinely-interactive surfaces
 * (booking, lead form) that need client JS regardless. Every other view is
 * statically imported + server-rendered.
 *
 * Each lazy component keeps the SAME props as its underlying view, so the
 * registry's `renderView` closures are unchanged.
 */
export const CalendarButtonView = dynamic(
  () =>
    import('../variants/calendar/components/CalendarButtonView').then(
      module => module.CalendarButtonView,
    ),
  { ssr: false },
);

export const StandaloneLeadsView = dynamic(
  () =>
    import('../variants/standalone-leads/components/StandaloneLeadsView').then(
      module => module.StandaloneLeadsView,
    ),
  { ssr: false },
);

// Interactive poll voting — pulls in `searchStorePollVotes` +
// `pollApiControllerVotePoll`, which the pure server-rendered bio has
// no need for until the visitor actually taps a poll option. Lazy so
// the client bundle stays out of first-paint.
export const LivePollButton = dynamic(
  () =>
    import('@ps/features/poll/voting/LivePollButton').then(
      module => module.LivePollButton,
    ),
  { ssr: false },
);
